/**
 * AuthTokenHandler
 * 
 * Captures auth tokens from cookies set on the shared base domain
 * (e.g. .cdsn.me / .codedesign.ai) by the accounts app.
 * 
 * The accounts app sets two cookies before redirecting:
 *   - token:  JWT auth token
 *   - userId: User ID (e.g. 6971e4032c5c98971144e1a5)
 * 
 * This component reads those cookies, stores them in localStorage,
 * fetches user profile + default workspace, then lets the app boot normally.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelectionStore } from '@/state/selection.store'
import { safeLocalStorage } from '@/lib/safe-storage'

const API_BETA = import.meta.env.VITE_API_BASE_URL

function getCookie(name) {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

export default function AuthTokenHandler() {
  const navigate = useNavigate()
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    // Skip if already authenticated with full data
    if (safeLocalStorage.getItem('auth_token') && safeLocalStorage.getItem('user_data')) return

    const token = getCookie('token')
    const userId = getCookie('userId')

    if (!token) return

    // Avoid running twice
    if (processing) return
    setProcessing(true)

    async function bootstrapAuth() {
      try {
        // 1. Store token and userId in localStorage
        safeLocalStorage.setItem('auth_token', token)
        if (userId) {
          safeLocalStorage.setItem('uRI', userId)
        }

        // 2. Fetch default workspace to get user info and workspace ID
        const wsResp = await fetch(`${API_BETA}workspaces/default`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': token,
          },
        })

        if (wsResp.ok) {
          const wsResult = await wsResp.json()

          // Extract workspace ID
          let workspaceId = wsResult?.data?._id || wsResult?.data?.id || wsResult?._id || wsResult?.id || null
          if (workspaceId) {
            useSelectionStore.getState().setActiveWorkspace(workspaceId)
          }
        }

        // 3. Build user_data by fetching user profile from user/:userId endpoint
        let userData = { id: userId, name: 'User', email: '', role: 'user' }

        if (userId) {
          try {
            const userResp = await fetch(`${API_BETA}user/${userId}/`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'x-access-token': token,
              },
            })

            if (userResp.ok) {
              const userResult = await userResp.json()
              const profile = userResult?.data || userResult
              userData = {
                id: profile._id || profile.id || userId,
                name: profile.name || 'User',
                email: profile.email || '',
                role: 'user',
                isAdmin: profile.isAdmin || false,
              }
            }
          } catch (e) {
            console.warn('[AuthTokenHandler] Could not fetch user profile:', e)
          }
        }

        safeLocalStorage.setItem('user_data', JSON.stringify(userData))

        // 4. Reload the page so AuthProvider picks up the new localStorage data
        window.location.reload()
      } catch (error) {
        console.error('[AuthTokenHandler] Error bootstrapping auth:', error)
        setProcessing(false)
      }
    }

    bootstrapAuth()
  }, [])

  return null
}
